import { useCallback, useEffect, useRef, useState } from "react";

// Owns the left-pane sizing state for a ProjectView:
//   * width (resizable, persisted per project)
//   * collapsed (toggle, persisted per project)
//   * startResize(e) — attach to the drag handle's mousedown
// Keeps drag logic DOM-local so ProjectView doesn't have to juggle listeners.
export default function useLeftPane(projectId, { minWidth = 280, maxWidth = 1100, defaultWidth = 620 } = {}) {
  const LS_WIDTH = `pg_leftpane_${projectId}`;
  const LS_COLLAPSED = `pg_leftpane_collapsed_${projectId}`;

  const [width, setWidth] = useState(() => {
    const v = parseInt(localStorage.getItem(LS_WIDTH) || "", 10);
    return Number.isFinite(v) && v >= minWidth ? v : defaultWidth;
  });
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(LS_COLLAPSED) === "1",
  );

  useEffect(() => {
    localStorage.setItem(LS_COLLAPSED, collapsed ? "1" : "0");
  }, [collapsed, LS_COLLAPSED]);

  const dragRef = useRef(null);

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: width };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      let latest = width;
      const onMove = (ev) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const next = Math.max(minWidth, Math.min(maxWidth, dragRef.current.startWidth + dx));
        latest = next;
        setWidth(next);
      };
      const cleanup = () => {
        dragRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", cleanup);
        window.removeEventListener("blur", cleanup);
        localStorage.setItem(LS_WIDTH, String(latest));
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", cleanup);
      window.addEventListener("blur", cleanup);
    },
    [width, minWidth, maxWidth, LS_WIDTH],
  );

  const resetWidth = useCallback(() => {
    setWidth(defaultWidth);
    localStorage.setItem(LS_WIDTH, String(defaultWidth));
  }, [defaultWidth, LS_WIDTH]);

  const toggleCollapsed = useCallback(() => setCollapsed((v) => !v), []);

  return {
    width,
    setWidth,
    collapsed,
    setCollapsed,
    toggleCollapsed,
    startResize,
    resetWidth,
  };
}
